import { isDate, isNumber, isNumerical, isString } from '../is'
import { condcat } from '../string'
import * as fns from 'date-fns'

const DAY_NAMES: string[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
]

const MONTH_NAMES: { abbr: string; name: string }[] = [
  {
    abbr: 'Jan',
    name: 'January'
  },
  {
    abbr: 'Feb',
    name: 'February'
  },
  {
    abbr: 'Mar',
    name: 'March'
  },
  {
    abbr: 'Apr',
    name: 'April'
  },
  {
    abbr: 'May',
    name: 'May'
  },
  {
    abbr: 'Jun',
    name: 'June'
  },
  {
    abbr: 'Jul',
    name: 'July'
  },
  {
    abbr: 'Aug',
    name: 'August'
  },
  {
    abbr: 'Sep',
    name: 'September'
  },
  {
    abbr: 'Oct',
    name: 'October'
  },
  {
    abbr: 'Nov',
    name: 'November'
  },
  {
    abbr: 'Dec',
    name: 'December'
  }
]

export const dateIsInvalid = (x: any): x is Date => {
  if (!x) return true
  if (!isDate(x)) return true
  if (x.toString().toLowerCase().includes('invalid')) return true
  return false
}

export const dateIsValid = (x: any): x is Date => dateIsInvalid(x) === false

export const formatDate = (date: Date): string => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ]
  const dateobj = new Date(date)
  const y = dateobj.getFullYear()
  const m = dateobj.getMonth()
  const d = dateobj.getDate()
  if (isNaN(y) || isNaN(m) || isNaN(d)) return date.toString()
  return `${d} ${months[m].substring(0, 3)}. ${y}`
}

export interface PrettyDateOptions {
  abbrev?: boolean
  y?: boolean
  m?: boolean
  d?: boolean
  D?: boolean
}
export const prettyDate = (
  date: Date,
  options: PrettyDateOptions = { abbrev: true, y: true, m: true, d: true }
) => {
  const y = date.getFullYear()
  const m = date.getMonth()
  const d = date.getDate()
  const monthTranslation = MONTH_NAMES[m]
  const monthName = options.abbrev ? monthTranslation.abbr : monthTranslation.name

  return condcat(
    [
      [options.m, monthName],
      [options.d && !options.D, `${d}`],
      [
        options.D && !options.d,
        options.abbrev
          ? Array.from(DAY_NAMES[d % DAY_NAMES.length])
              .splice(0, 3)
              .join('')
          : DAY_NAMES[d % DAY_NAMES.length]
      ],
      [options.y, y.toString()]
    ],
    ' '
  )
}

export const parseDate = (value: any): Date => {
  if (!value) return new Date('Invalid Date')
  if (isDate(value)) return value
  if (isString(value)) {
    let d = new Date(value)
    if (dateIsValid(d)) return d
    if (isNumerical(value)) {
      d = new Date(parseFloat(value))
      if (dateIsValid(d)) return d
    }
    d = new Date(Date.parse(value))
    if (dateIsValid(d)) return d
    return fns.parseISO(value)
  }
  if (isNumber(value)) return new Date(value)
  return new Date('Invalid Date')
}

export const lineChartDateFormat = (date: Date): string => {
  //var mo = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
  const year = date.getFullYear()
  const month = MONTH_NAMES[date.getMonth()] ? MONTH_NAMES[date.getMonth()].abbr : 'undefined'
  const day = date.getDate().toString().padStart(2, '0')
  return `${day}-${month}-${year}`
}

const DATE_VARIANTS = [
  'HH:mm:s dd MMM, yyy',
  'dd MMM, HH:mm, yyy',
  'dd MMM, yyy',
  'HH:mm:ss'
]

export const dateFmt = (date: Date | string | number, variant: number = 0): string => {
  const d = parseDate(date);
  return fns.format(d, DATE_VARIANTS[variant % DATE_VARIANTS.length]);
}

export const fmtDate = dateFmt;

export enum EDateFormat {
  MONTH_DAY = 'MMM, dd',
  MONTH_DAY_YEAR = 'MMM dd, yyy',
  DAY_MONTH_YEAR = 'dd MMM, yyy',
  DAY_MONTH_YEAR_HOUR_MINUTE = 'dd MMM, yyy HH:mm',
  MONTH_DAY_YEAR_HOUR_MINUTE = 'MMM dd, yyy HH:mm',
  MONTH_DAY_YEAR_HOUR_MINUTE_SECOND = 'MMM dd, yyy HH:mm:ss',
}

export const dateToHumanReadable = (date: Date | string | number, fmt: EDateFormat): string =>  {
  try {
    const d = parseDate(date);
    return fns.format(d, fmt);
  } catch (e) {
    console.error(e);
    return 'N/A';
  }
}

export const formatRelativeDate = (date: Date | string | number): string => {
  const d = parseDate(date);

  if (!dateIsValid(d)) {
    return 'Invalid date';
  }

  let text = '';

  if (fns.isToday(d)) {
    text = 'Today';
  } else if (fns.isYesterday(d)) {
    text = 'Yesterday';
  } else {
    text = fns.formatDistanceToNow(d, { addSuffix: true });
  }

  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const parseDateInput = (value: string): Date | null => {
	if (!value || typeof value !== 'string') return null

	const pattern = /^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/
	const match = value.match(pattern)

	let day: number, month: number, year: number

	if (match) {
		day = parseInt(match[1], 10)
		month = parseInt(match[2], 10) - 1
		year = parseInt(match[3], 10)
	} else {
		const digits = value.replace(/\D/g, '')
		if (digits.length !== 8) return null
		day = parseInt(digits.slice(0, 2), 10)
		month = parseInt(digits.slice(2, 4), 10) - 1
		year = parseInt(digits.slice(4, 8), 10)
	}

	if (day < 1 || day > 31 || month < 0 || month > 11 || year <= 0) return null

	const d = new Date(year, month, day)
	if (!dateIsValid(d)) return null

	if (d.getDate() === day && d.getMonth() === month && d.getFullYear() === year) {
		return d
	}

	return null
}

export const formatDateToDDMMYYYY = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

